import React, { ReactElement, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { MoreIconCMS } from 'src/assets/MoreIconCMS';
import { Switch } from 'src/domains/common/components/switch/switch';
import { MenuItemWrapper, MenuWrapper } from 'src/domains/common/components/menu-bar/menu-bar';
import { DropdownMenuState } from 'src/components/GlobalPageV2/DropdownMenu/DropdownMenu.state';
import { observer } from 'src/utils/mobx-react';
import classNames from 'classnames';
import { MarkersOfHarmPopupType } from './MarkersOfHarmPopupTab.types';

const formatListDate = (isoDate: string): string => {
    return new Date(isoDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

const buildPopupMetaLine = (popup: MarkersOfHarmPopupType): string => {
    const parts = [`Created: ${formatListDate(popup.createdAt)}`];

    if (popup.active && popup.activationDate !== undefined) {
        parts.push(`Active since: ${formatListDate(popup.activationDate)}`);
    } else if (!popup.active && popup.deactivationDate !== undefined) {
        parts.push(`Last active: ${formatListDate(popup.deactivationDate)}`);
    }

    return parts.join(', ');
};

type MarkersOfHarmPopupListItemPropsType = {
    popup: MarkersOfHarmPopupType;
    isSelected: boolean;
    isToggling: boolean;
    onEdit: (popup: MarkersOfHarmPopupType) => void;
    onShow: (popup: MarkersOfHarmPopupType) => void;
    onToggleActive: (popup: MarkersOfHarmPopupType, active: boolean) => void;
};

export const MarkersOfHarmPopupListItem = observer(
    'MarkersOfHarmPopupListItem',
    ({
        popup,
        isSelected,
        isToggling,
        onEdit,
        onShow,
        onToggleActive,
    }: MarkersOfHarmPopupListItemPropsType): ReactElement => {
        const [dropdownState] = useState(() => new DropdownMenuState());
        const menuAnchorRef = useRef<HTMLDivElement | null>(null);
        const menuPanelRef = useRef<HTMLDivElement | null>(null);
        const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

        useEffect(() => {
            if (!dropdownState.isOpen) {
                return;
            }

            const handleClickOutside = (event: MouseEvent): void => {
                const target = event.target;
                if (!(target instanceof Node)) {
                    return;
                }

                if (menuAnchorRef.current?.contains(target) === true) {
                    return;
                }

                if (menuPanelRef.current?.contains(target) === true) {
                    return;
                }

                dropdownState.closeDropdownMenu();
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [dropdownState, dropdownState.isOpen]);

        const handleToggleMenu = (): void => {
            if (!dropdownState.isOpen && menuAnchorRef.current !== null) {
                const rect = menuAnchorRef.current.getBoundingClientRect();
                setMenuPosition({
                    top: rect.bottom + 4,
                    right: window.innerWidth - rect.right,
                });
            }
            dropdownState.toggleDropdownMenu();
        };

        const handleEdit = (): void => {
            dropdownState.closeDropdownMenu();
            onEdit(popup);
        };

        const handlePreview = (): void => {
            dropdownState.closeDropdownMenu();
            onShow(popup);
        };

        const handleToggleActive = (event: React.ChangeEvent<HTMLInputElement>): void => {
            onToggleActive(popup, event.target.checked);
        };

        return (
            <li
                className={classNames(
                    'flex items-center justify-between gap-3 rounded px-3 py-3',
                    isSelected ? 'bg-muted' : 'bg-background'
                )}
                data-test={`markers-of-harm-popup-list-item-${popup.id}`}
            >
                <div className='min-w-0 flex-auto'>
                    <p className='m-0 flex min-w-0 items-baseline gap-2 text-sm font-medium'>
                        <span className='truncate max-w-[300px]'>{popup.title}</span>
                        <span className='shrink-0 text-xs font-normal text-primary'>moh-{popup.triggerId}</span>
                    </p>
                    <p className='m-0 mt-1 text-xs text-muted-foreground'>{buildPopupMetaLine(popup)}</p>
                </div>

                <div className='flex items-center gap-3 shrink-0'>
                    <Switch
                        label={popup.active ? 'Enabled' : 'Disabled'}
                        labelPosition='left'
                        checked={popup.active}
                        disabled={isToggling}
                        onChange={handleToggleActive}
                        dataTest={`markers-of-harm-popup-active-switch-${popup.id}`}
                    />

                    <div ref={menuAnchorRef}>
                        <button
                            type='button'
                            className='inline-flex size-8 cursor-pointer items-center border-none  justify-center border-input bg-background '
                            data-test={`markers-of-harm-popup-menu-trigger-${popup.id}`}
                            onClick={handleToggleMenu}
                            aria-label='Open Pop-Up actions'
                        >
                            <MoreIconCMS />
                        </button>
                    </div>

                    {dropdownState.isOpen && menuPosition !== null
                        ? ReactDOM.createPortal(
                              <div
                                  ref={menuPanelRef}
                                  className='fixed z-50'
                                  style={{ top: menuPosition.top, right: menuPosition.right }}
                              >
                                  <MenuWrapper
                                      className='w-36 p-1'
                                      data-test={`markers-of-harm-popup-menu-panel-${popup.id}`}
                                  >
                                      <MenuItemWrapper
                                          label='Edit'
                                          showChevron={false}
                                          className='hover:bg-accent'
                                          data-test={`markers-of-harm-popup-edit-${popup.id}`}
                                          onClick={handleEdit}
                                      />
                                      <MenuItemWrapper
                                          label='Preview'
                                          showChevron={false}
                                          className='hover:bg-accent'
                                          data-test={`markers-of-harm-popup-preview-${popup.id}`}
                                          onClick={handlePreview}
                                      />
                                  </MenuWrapper>
                              </div>,
                              document.body
                          )
                        : null}
                </div>
            </li>
        );
    }
);
